import type { EditorSDK, PageRef, RouterData, RouterRef } from '@wix/platform-editor-sdk';
import _ from 'lodash';

import type { RouterPatterns } from '../../types/EditorAppModule';
import { APP_TOKEN, NEW_PAGE_ROUTE } from '../constants';

async function getPageRouterData(editorSDK: EditorSDK, pageRef: PageRef) {
  const routerRef = await editorSDK.routers.getByPage(APP_TOKEN, { pageRef });
  const router = await editorSDK.routers.get(APP_TOKEN, { routerRef });

  if (!router) {
    throw new Error('Could not retrieve the router in #getPageRouterData');
  }

  const pageRouterData = _.find(router.pages, (page) => page.pageRef.id === pageRef.id);
  const guid = pageRouterData?.pageRoles[0];
  // TODO Fix RouterData config patterns type in editor platform sdk
  const data: any = _.find(router.config.patterns as unknown as RouterPatterns, (p) => p.page === guid);
  const pattern = _.findKey(router.config.patterns as unknown as RouterPatterns, (p) => p.page === guid);
  if (data) {
    data.routerRef = routerRef;
    data.innerRoute = pattern;
    data.prefix = router.prefix;
    data.isPrivate = router.config.type === 'private';
  }

  return data;
}

async function updatePageData(
  editorSDK: EditorSDK,
  pageRef: PageRef,
  dataFieldToUpdate: string,
  updatedData: string | object,
) {
  const routerRef = await editorSDK.routers.getByPage(APP_TOKEN, { pageRef });
  const router = await editorSDK.routers.get(APP_TOKEN, { routerRef });
  const pageRouterData = _.find(router.pages, (page) => {
    return page.pageRef.id === pageRef.id;
  });
  const guid = pageRouterData?.pageRoles[0];
  const pattern = _.findKey(router.config.patterns as unknown as RouterPatterns, (p: any) => {
    return p.page === guid;
  });
  const patternData = _.find(router.config.patterns as unknown as RouterPatterns, (p) => {
    return p.page === guid;
  });

  patternData![dataFieldToUpdate] =
    typeof updatedData === 'string' ? updatedData : _.assign({}, patternData![dataFieldToUpdate], updatedData);

  const config = router.config;
  _.set(config, 'patterns[' + pattern + ']', patternData);

  await editorSDK.routers.update(APP_TOKEN, {
    routerRef,
    config,
  });
  return routerRef;
}

async function changePagePattern(editorSDK: EditorSDK, pageRef: PageRef, newSuffix: string) {
  const routerRef = await editorSDK.routers.getByPage(APP_TOKEN, { pageRef });
  const router = await editorSDK.routers.get(APP_TOKEN, { routerRef });
  const pageRouterData = _.find(router.pages, (page) => {
    return page.pageRef.id === pageRef.id;
  });
  const guid = pageRouterData?.pageRoles[0];
  const pattern = _.findKey(router.config.patterns as unknown as RouterPatterns, (p) => {
    return p.page === guid;
  });
  const patternData = _.find(router.config.patterns as unknown as RouterPatterns, (p) => {
    return p.page === guid;
  });

  const config = router.config;
  const newPattern = (config.type === 'private' ? '/' : '/{userName}/') + newSuffix;
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete config.patterns[pattern!];
  _.set(config, 'patterns[' + newPattern + ']', patternData);

  await editorSDK.routers.update(APP_TOKEN, {
    routerRef,
    config,
  });
  return newPattern;
}

async function getId(editorSDK: EditorSDK, routerRef: RouterRef) {
  const router = await editorSDK.routers.get(APP_TOKEN, { routerRef });
  return router.id;
}

async function getAll(editorSDK: EditorSDK) {
  const routersArr = await editorSDK.routers.getAll(APP_TOKEN);
  return routersArr;
}

async function add(
  editorSDK: EditorSDK,
  routerDef: Parameters<EditorSDK['routers']['add']>[1],
  retry = 0,
  e?: Error | string,
) {
  if (retry > 3) {
    throw new Error('Can not add router, failed after 3 retries, reason: ' + e && e?.toString());
  }

  const retryAddingRouter = async (previousError: Error | string) => {
    // Sometimes SDK adds a router but still throws some kind of error, and this method retries.
    // If router is already added in our app scope, let's not add it again
    const existingRouter = await editorSDK.routers.getByPrefix(APP_TOKEN, { prefix: routerDef.prefix });

    if (existingRouter) {
      return existingRouter;
    }

    let { prefix: routerPrefix } = routerDef;
    /* eslint-disable-next-line no-useless-escape */
    routerPrefix = routerPrefix.replace(/\-[0-9]$/, '');
    routerPrefix += `-${++retry}`;
    routerDef.prefix = routerPrefix;
    return add(editorSDK, routerDef, retry, previousError);
  };

  const router = await editorSDK.routers.add(APP_TOKEN, routerDef).catch(retryAddingRouter);

  // Sometimes SDK resolves a router but does not actually add it because of pageUriSEO is already taken
  const addedRouter = await editorSDK.routers.get(APP_TOKEN, { routerRef: router });
  if (!addedRouter) {
    return retryAddingRouter('Router was not added although the installation was successful');
  }

  return router;
}

async function removeAllRouters(editorSDK: EditorSDK) {
  const routers = await editorSDK.routers.getAll(APP_TOKEN);
  for (const routerData of routers) {
    const routerRef = await editorSDK.routers.getByPrefix(APP_TOKEN, { prefix: routerData.prefix });
    await editorSDK.routers.remove(APP_TOKEN, { routerRef });
  }
}

async function removeConnectedPages(editorSDK: EditorSDK) {
  const routersArr = await getAll(editorSDK);
  for (const routerData of routersArr) {
    const routerRef = await editorSDK.routers.getByPrefix(APP_TOKEN, { prefix: routerData.prefix });
    for (const page of routerData.pages) {
      // @ts-expect-error TODO: fix types in editor platform sdk - pageToNavigateAfterRemove should be optional
      await editorSDK.routers.pages.remove(APP_TOKEN, {
        routerRef,
        pageRef: page.pageRef,
      });
    }
  }
}

async function findPageRefByAppData(editorSDK: EditorSDK, appDefinitionId: string, appPageId: string) {
  const routers = await getAll(editorSDK);
  for (const router of routers) {
    const pattern = _.find(
      router.config.patterns as unknown as RouterPatterns,
      (p) => _.get(p, 'appData.appDefinitionId') === appDefinitionId && _.get(p, 'appData.appPageId') === appPageId,
    );
    if (pattern) {
      for (const page of router.pages) {
        if (page.pageRoles.includes(pattern.page)) {
          return page.pageRef;
        }
      }
    }
  }
  throw new Error(`Could not find member page for TPA ${appDefinitionId} and section ${appPageId}`);
}

const getPatternByPageRef = (pageRef: PageRef, router: RouterData) => {
  const pageRoles = router.pages.find((page) => page.pageRef?.id === pageRef.id)?.pageRoles;
  if (!pageRoles?.length || !router.config.patterns) {
    return;
  }
  const pattern = Object.keys(router.config.patterns).find((onePattern) =>
    pageRoles.includes(router.config.patterns[onePattern]?.page),
  );

  return pattern;
};

function createNewPageRoute(allRoutes: string[], slug = NEW_PAGE_ROUTE) {
  let route = `/${slug}`;
  let i = 1;
  while (allRoutes.includes(route)) {
    route = `/${slug}-${i}`;
    i++;
  }
  return route.substring(1);
}

function getRouterRefByPrefix({ editorSDK, prefix }: { editorSDK: EditorSDK; prefix: string }) {
  return editorSDK.routers.getByPrefix(APP_TOKEN, { prefix });
}

function connectPageToRouter({
  editorSDK,
  pageRef,
  pageRoles,
  routerRef,
}: {
  editorSDK: EditorSDK;
  pageRef: PageRef;
  pageRoles: string[] | undefined;
  routerRef: RouterRef;
}) {
  return editorSDK.routers.pages.connect(APP_TOKEN, { pageRef, routerRef, pageRoles });
}

function updateRouterConfig({
  editorSDK,
  config,
  routerRef,
}: {
  editorSDK: EditorSDK;
  routerRef: RouterRef;
  config: any;
}) {
  return editorSDK.routers.update(APP_TOKEN, { config, routerRef });
}

function removeRouterPage({
  editorSDK,
  routerRef,
  pageRef,
  pageToNavigateAfterRemove,
}: {
  editorSDK: EditorSDK;
  routerRef: RouterRef;
  pageRef: PageRef;
  pageToNavigateAfterRemove: PageRef;
}) {
  return editorSDK.routers.pages.remove(APP_TOKEN, { routerRef, pageRef, pageToNavigateAfterRemove });
}

function getByPageRef({ editorSDK, pageRef }: { editorSDK: EditorSDK; pageRef: PageRef }) {
  return editorSDK.document.routers.getByPage('', { pageRef });
}

function getByRef({ editorSDK, routerRef }: { editorSDK: EditorSDK; routerRef: RouterRef }) {
  return editorSDK.document.routers.get('', { routerRef });
}

function refreshRouters(editorSDK: EditorSDK) {
  return editorSDK.editor.routers.refresh(APP_TOKEN);
}

export {
  add,
  changePagePattern,
  connectPageToRouter,
  createNewPageRoute,
  findPageRefByAppData,
  getAll,
  getByPageRef,
  getByRef,
  getId,
  getPageRouterData,
  getPatternByPageRef,
  getRouterRefByPrefix,
  refreshRouters,
  removeAllRouters,
  removeConnectedPages,
  removeRouterPage,
  updatePageData,
  updateRouterConfig,
};
